"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { AppWrap, MotionWrap } from "../../wrapper";
import "./About.scss";
import { images } from "../../constants";

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.45, ease: "easeOut" },
  },
  exit: (direction) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
    transition: { duration: 0.35, ease: "easeIn" },
  }),
};

const textVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.25,
      duration: 0.7,
      ease: "easeOut",
    },
  }),
};

const About = () => {
  const aboutText = [
    "BBM Cyberspace Ltd is committed to empowering organisations and individuals with the knowledge, tools, and strategies needed to navigate today’s rapidly evolving cybersecurity landscape. Over the past eight years, we have specialised in governance, risk, and compliance (GRC), helping organisations strengthen their security frameworks while ensuring they meet national and global regulatory standards.",
    "Through strategic international partnerships, we have worked with numerous organisations across multiple sectors to successfully secure their PCI DSS certifications. This hands-on experience has positioned us as a trusted compliance partner, guiding businesses from assessment to full certification while enhancing their operational resilience and payment security.",
    "Our support has consistently helped clients reduce risk exposure, improve trust with their customers, and maintain robust controls aligned with global best practices.",
    "In addition to consultancy, we provide industry-leading cybersecurity training programmes aimed at closing the skills gap. These programmes are practical, immersive, and designed to ensure learners can confidently enter and excel in cybersecurity roles.",
    "With our recently secured intellectual property approval, BBM Cyberspace Ltd continues to expand its holistic approach—integrating risk management, compliance advisory, security awareness, workforce development, and strategic capacity-building.",
    "Whether your goal is to strengthen your organisation’s security posture, achieve compliance, or build a sustainable cybersecurity career pipeline, BBM Cyberspace Ltd remains your trusted partner for long-term resilience, growth, and success.",
  ];

  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // carousel state
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // detect screen
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const goNext = () => {
    setDirection(1);
    setIndex((prev) => (prev + 1) % aboutText.length);
  };

  const goPrev = () => {
    setDirection(-1);
    setIndex((prev) => (prev - 1 + aboutText.length) % aboutText.length);
  };

  const [abouts] = useState([
    {
      imgUrl: images.abdullateef,
      title: "Abdullateef Hameed",
      jobTitle: "Managing Director",
    },
    {
      imgUrl: images.fawziyat,
      title: "Fawziyat Lukman",
      jobTitle: "Linguistic Intelligence",
    },
    {
      imgUrl: images.nneka,
      title: "Nneka Machukwu",
      jobTitle: "Multilingual Cyber Threat Researcher",
    },
    {
      imgUrl: images.abdulsamad,
      title: "Abdulsamad Mustapha",
      jobTitle: "Phishing Content Analyst",
    },
  ]);

  return (
    <>
      <h2 className="head-text flex gap-2">
        About <span>Us</span>
      </h2>

      <div className="app__about-intro app__primarybg !p-6 rounded-xl">
        {/* ---------------- MOBILE CAROUSEL ---------------- */}
        {isMobile ? (
          <div className="relative">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.p
                key={index}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="app__about-paragraph sec__color min-h-[160px]"
              >
                {aboutText[index]}
              </motion.p>
            </AnimatePresence>

            {/* NAVIGATION BUTTONS */}
            <div className="flex justify-between items-center mt-4">
              <button
                onClick={goPrev}
                className="!px-3 !py-2 rounded-lg p-text transition"
              >
                ‹ Prev
              </button>

              <span className="text-sm p-text font-semibold">
                {index + 1} / {aboutText.length}
              </span>

              <button
                onClick={goNext}
                className="!px-3 !py-2 rounded-lg p-text transition"
              >
                Next ›
              </button>
            </div>
          </div>
        ) : (
          /* ---------------- DESKTOP VIEW ---------------- */
          <>
            {aboutText.slice(0, 2).map((paragraph, i) => (
              <motion.p
                key={i}
                custom={i}
                variants={textVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="app__about-paragraph sec__color"
              >
                {paragraph}
              </motion.p>
            ))}

            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  {aboutText.slice(2).map((paragraph, i) => (
                    <motion.p
                      key={i}
                      className="app__about-paragraph sec__color"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6 }}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-4 font-semibold sec__color underline text-left !cursor-pointer"
            >
              {expanded ? "Read Less" : "Read More"}
            </button>
          </>
        )}
      </div>

      {/* ---------------- TEAM SECTION ---------------- */}
      <div className="app__profiles">
        {abouts.map((about, index) => (
          <motion.div
            whileInView={{ opacity: 1 }}
            whileHover={{ scale: 1.08 }}
            transition={{ duration: 0.5, type: "tween" }}
            className="app__profile-item"
            key={about.title + index}
          >
            <img src={about.imgUrl} alt={about.title} />
            <h2 className="bold-text mt-4">{about.title}</h2>
            <p className="p-text mt-2 text-center">{about.jobTitle}</p>
          </motion.div>
        ))}
      </div>
    </>
  );
};

export default AppWrap(
  MotionWrap(About, "app__about"),
  "about",
  "app__whitebg"
);
